import React from 'react';

import { createStyles, Group, NumberInput, Paper, Select, Stack, Text, TextInput } from '@mantine/core';
import {
  DISTANCE_UNIT_OPTIONS,
  DistanceUnitEnum,
  ScoreTypeEnum,
  WEIGHT_UNIT_LABEL_MAP,
  WEIGHT_UNIT_OPTIONS,
  WeightUnitEnum,
} from 'types/enums';

import { SectionMovement, SectionMovementScoreSet } from '../../../../../forms/workoutBuilder/types';

interface MovementScoringConfigProps {
  movement: SectionMovement;
  onChange: (updates: Partial<SectionMovement>) => void;
  dataCyPrefix: string;
}

const useStyles = createStyles(theme => ({
  card: {
    backgroundColor: theme.fn.rgba(theme.colors.blue[1], 0.2),
    border: `1px solid ${theme.colors.blue[2]}`,
  },
  caption: {
    color: theme.colors.blue[6],
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  twoPane: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, auto) 1fr',
    gap: theme.spacing.lg,
    alignItems: 'start',
  },
  paneDivider: {
    borderLeft: `1px solid ${theme.colors.blue[2]}`,
    paddingLeft: theme.spacing.lg,
  },
  setRow: {
    display: 'grid',
    gridTemplateColumns: '48px auto auto auto auto',
    justifyContent: 'start',
    gap: theme.spacing.xs,
    alignItems: 'center',
  },
}));

const Caption = ({ children, className }: { children: React.ReactNode; className: string }): JSX.Element => (
  <Text className={className} mb='xs'>
    ↓ {children}
  </Text>
);

const MovementScoringConfig = ({
  movement,
  onChange,
  dataCyPrefix,
}: MovementScoringConfigProps): JSX.Element | null => {
  const { classes } = useStyles();
  const { scoreType, distanceUnit, weightUnit, scoreSets } = movement;

  if (scoreType === null) return null;

  const renderPreview = (): JSX.Element => {
    switch (scoreType) {
      case ScoreTypeEnum.CALORIES:
        return (
          <Group spacing='xs' align='center'>
            <TextInput placeholder='Calories' readOnly style={{ width: 120 }} />
            <Text size='sm'>kcal</Text>
          </Group>
        );

      case ScoreTypeEnum.DISTANCE: {
        return (
          <Group spacing='xs' align='center'>
            <TextInput placeholder='Distance' readOnly style={{ width: 120 }} />
            <Select
              data={DISTANCE_UNIT_OPTIONS}
              value={distanceUnit}
              onChange={value => onChange({ distanceUnit: (value as DistanceUnitEnum) || null })}
              style={{ width: 110 }}
              aria-label='Distance unit'
              data-cy={`${dataCyPrefix}-distanceUnit`}
            />
          </Group>
        );
      }

      case ScoreTypeEnum.REPS:
        return (
          <Group spacing='xs' align='center'>
            <TextInput placeholder='Reps' readOnly style={{ width: 120 }} />
            <Text size='sm'>reps</Text>
          </Group>
        );

      case ScoreTypeEnum.ROUNDS:
        return (
          <Group spacing='xs' align='center'>
            <TextInput placeholder='Rounds' readOnly style={{ width: 120 }} />
            <Text size='sm'>rounds</Text>
          </Group>
        );

      case ScoreTypeEnum.ROUNDS_AND_REPS:
        return (
          <Group spacing='xs' align='center'>
            <TextInput placeholder='Rounds' readOnly style={{ width: 100 }} />
            <Text size='sm'>rounds</Text>
            <Text size='sm' weight={500}>
              +
            </Text>
            <TextInput placeholder='Reps' readOnly style={{ width: 100 }} />
            <Text size='sm'>reps</Text>
          </Group>
        );

      case ScoreTypeEnum.TIME_HIGHER_BETTER:
      case ScoreTypeEnum.TIME_LOWER_BETTER:
        return (
          <Group spacing='xs' align='center'>
            <TextInput placeholder='Min' readOnly style={{ width: 80 }} />
            <Text size='lg' weight={500}>
              :
            </Text>
            <TextInput placeholder='Sec' readOnly style={{ width: 80 }} />
          </Group>
        );

      case ScoreTypeEnum.WEIGHT_SETS_REPS:
        return renderWeightStructure();

      default:
        return <></>;
    }
  };

  const totalSets = scoreSets.length || 1;
  const globalReps = scoreSets[0]?.targetReps ?? 1;
  const unitLabel = weightUnit
    ? WEIGHT_UNIT_LABEL_MAP[weightUnit]
    : WEIGHT_UNIT_LABEL_MAP[WeightUnitEnum.LB];

  const handleSetsCountChange = (value: number | ''): void => {
    if (value === '' || value < 1) return;
    const repsToUse = globalReps;
    const nextSets: SectionMovementScoreSet[] = Array.from({ length: value }, (_, i) => {
      const existing = scoreSets[i];
      return existing ? { ...existing, setIndex: i } : { setIndex: i, targetReps: repsToUse };
    });
    onChange({ scoreSets: nextSets });
  };

  const handleGlobalRepsChange = (value: number | ''): void => {
    if (value === '' || value < 1) return;
    const count = totalSets;
    const nextSets: SectionMovementScoreSet[] = Array.from({ length: count }, (_, i) => ({
      setIndex: i,
      targetReps: value,
    }));
    onChange({ scoreSets: nextSets });
  };

  const handlePerSetRepsChange = (setIndex: number, value: number | ''): void => {
    if (value === '' || value < 1) return;
    const next = scoreSets.map(s => (s.setIndex === setIndex ? { ...s, targetReps: value } : s));
    onChange({ scoreSets: next });
  };

  function renderWeightStructure(): JSX.Element {
    return (
      <div className={classes.twoPane}>
        <div>
          <Text className={classes.caption} mb='xs'>
            Set workout structure
          </Text>
          <Group spacing='xs' align='flex-end' noWrap mb={4}>
            <Stack spacing={2} align='center'>
              <NumberInput
                min={1}
                value={totalSets}
                onChange={handleSetsCountChange}
                style={{ width: 64 }}
                hideControls
                data-cy={`${dataCyPrefix}-totalSets`}
              />
              <Text size='xs' color='dimmed' weight={600}>
                # SETS
              </Text>
            </Stack>
            <Stack spacing={2} align='center'>
              <NumberInput
                min={1}
                value={globalReps}
                onChange={handleGlobalRepsChange}
                style={{ width: 64 }}
                hideControls
                data-cy={`${dataCyPrefix}-globalReps`}
              />
              <Text size='xs' color='dimmed' weight={600}>
                REPS
              </Text>
            </Stack>
          </Group>
          <Text size='xs' color='dimmed' italic>
            You can customize reps for each set on the right
          </Text>
        </div>
        <div className={classes.paneDivider}>
          <Caption className={classes.caption}>How athletes will log their results</Caption>
          <Stack spacing='xs'>
            {scoreSets.map((set, idx) => (
              <div key={set.setIndex} className={classes.setRow}>
                <Text size='sm' color='dimmed'>
                  Set {set.setIndex + 1}
                </Text>
                <Text size='sm' color='dimmed'>
                  →
                </Text>
                <NumberInput
                  min={1}
                  value={set.targetReps}
                  onChange={value => handlePerSetRepsChange(set.setIndex, value)}
                  style={{ width: 64 }}
                  hideControls
                  data-cy={`${dataCyPrefix}-set-${set.setIndex}-reps`}
                />
                <Group spacing='xs' align='center' noWrap>
                  <Text size='sm'>@</Text>
                  <TextInput placeholder='Weight' readOnly style={{ width: 100 }} />
                </Group>
                {idx === 0 ? (
                  <Select
                    data={WEIGHT_UNIT_OPTIONS}
                    value={weightUnit}
                    onChange={value => onChange({ weightUnit: (value as WeightUnitEnum) || null })}
                    style={{ width: 90 }}
                    aria-label='Weight unit'
                    data-cy={`${dataCyPrefix}-weightUnit`}
                  />
                ) : (
                  <Text size='sm' color='dimmed' pl='xs'>
                    {unitLabel}
                  </Text>
                )}
              </div>
            ))}
          </Stack>
        </div>
      </div>
    );
  }

  return (
    <Paper p='md' radius='md' className={classes.card}>
      {scoreType === ScoreTypeEnum.WEIGHT_SETS_REPS ? (
        renderWeightStructure()
      ) : (
        <>
          <Caption className={classes.caption}>How athletes will log their results</Caption>
          {renderPreview()}
        </>
      )}
    </Paper>
  );
};

export default MovementScoringConfig;
